import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import DataTable from "../../../components/shared/DataTable";
import Pagination from "../../../components/shared/Pagination";
import { formatDate, statusBadgeClass } from "../../../utils/helpers";
import api from "../../../utils/api";

export default function LabBookings() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  const { data, isLoading } = useQuery(
    ["lab-bookings", page, statusFilter, dateFilter],
    () =>
      api.get(
        `/lab/bookings?page=${page}&status=${statusFilter}&date=${dateFilter}`
      ),
    { keepPreviousData: true }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => api.post(`/lab/bookings/${id}/status`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("lab-bookings");
        toast.success("Status updated");
      },
    }
  );

  const columns = [
    { key: "patientName", label: "Patient" },
    { key: "tests", label: "Tests Requested" },
    {
      key: "scheduledAt",
      label: "Scheduled",
      render: (row) => formatDate(row.scheduledAt, "dd MMM, hh:mm a"),
      sortable: true,
    },
    { key: "collectionType", label: "Collection" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) =>
        row.status === "pending" || row.status === "confirmed" ? (
          <div className="flex gap-2">
            {row.status === "pending" && (
              <button
                onClick={() => updateStatusMutation.mutate({ id: row.id, status: "confirmed" })}
                className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                Confirm
              </button>
            )}
            <button
              onClick={() => updateStatusMutation.mutate({ id: row.id, status: "completed" })}
              className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              Complete
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="input-field w-auto" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field w-auto">
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <DataTable
        columns={columns}
        data={data?.bookings || []}
        loading={isLoading}
        emptyMessage="No bookings yet."
      />
      <Pagination page={page} totalPages={data?.totalPages || 1} onPageChange={setPage} />
    </div>
  );
}
